import React, { useEffect, useMemo, useState } from "react";
import { analyticsApi } from "../api/routes";
import { Button, Card, Input, JsonPreview, Select, Toast } from "../components/ui/Controls";

const defaultWindow = () => {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
    limit: "1000",
  };
};

function Stat({ label, value, tone = "neutral" }) {
  return (
    <div className={`analytics-stat analytics-stat-${tone}`}>
      <div className="analytics-stat-label">{label}</div>
      <div className="analytics-stat-value">{value ?? "n/a"}</div>
    </div>
  );
}

function metricValue(row, key) {
  return Number(row?.[key] || 0);
}

function totalDuration(row) {
  return metricValue(row, "durationMs") || metricValue(row, "modelDurationMs");
}

function averageDuration(row) {
  const count = metricValue(row, "count");
  return count > 0 ? totalDuration(row) / count : 0;
}

function p95Duration(row) {
  return metricValue(row, "p95DurationMs") || averageDuration(row);
}

function topRows(rows, limit = 8, valueFn = averageDuration) {
  return [...(rows || [])]
    .filter((row) => metricValue(row, "count") > 0)
    .sort((a, b) => valueFn(b) - valueFn(a))
    .slice(0, limit);
}

function BarList({ title, rows, valueLabel = formatMs, valueFn = p95Duration, countLabel = "events" }) {
  const visibleRows = topRows(rows, 8, valueFn);
  const max = Math.max(...visibleRows.map(valueFn), 0);
  return (
    <Card title={title}>
      {!visibleRows.length ? (
        <div className="analytics-empty">No timing data in this window.</div>
      ) : (
        <div className="analytics-bar-list">
          {visibleRows.map((row) => {
            const value = valueFn(row);
            const width = max > 0 ? Math.max(3, (value / max) * 100) : 0;
            return (
              <div className="analytics-bar-row" key={row.name}>
                <div className="analytics-bar-head">
                  <span>{row.name}</span>
                  <strong>{valueLabel(value)}</strong>
                </div>
                <div className="analytics-bar-track" aria-hidden="true">
                  <div className="analytics-bar-fill" style={{ width: `${width}%` }} />
                </div>
                <div className="analytics-bar-meta">
                  {formatNumber(row.count)} {countLabel}
                  {totalDuration(row) > 0 ? ` · total ${formatMs(totalDuration(row))}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ColdStartSplit({ rows }) {
  const cold = rows?.find((row) => String(row.name) === "true");
  const warm = rows?.find((row) => String(row.name) === "false");
  const total = metricValue(cold, "count") + metricValue(warm, "count");
  const coldPct = total > 0 ? (metricValue(cold, "count") / total) * 100 : 0;
  return (
    <Card title="Cold start split">
      {!total ? (
        <div className="analytics-empty">No Lambda invocation timing yet.</div>
      ) : (
        <div className="analytics-cold-start">
          <div className="analytics-cold-meter" aria-hidden="true">
            <div style={{ width: `${coldPct}%` }} />
          </div>
          <div className="analytics-cold-grid">
            <div>
              <span>Cold</span>
              <strong>{formatNumber(cold?.count)}</strong>
              <small>{formatMs(averageDuration(cold))} avg</small>
            </div>
            <div>
              <span>Warm</span>
              <strong>{formatNumber(warm?.count)}</strong>
              <small>{formatMs(averageDuration(warm))} avg</small>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function MetricTable({ title, rows, nameLabel }) {
  return (
    <Card title={title}>
      {!rows?.length ? (
        <div style={{ color: "var(--stone-700)" }}>No data in this window.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{nameLabel}</th>
                <th>Runs</th>
                <th>Errors</th>
                <th>Tokens</th>
                <th>Cost</th>
                <th>P50 ms</th>
                <th>P95 ms</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{row.count}</td>
                  <td>{row.errorCount}</td>
                  <td>{formatNumber(row.totalTokens)}</td>
                  <td>{formatUsd(row.estimatedCostUsd)}</td>
                  <td>{row.p50DurationMs ?? "n/a"}</td>
                  <td>{row.p95DurationMs ?? row.avgModelDurationMs ?? "n/a"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RollupTable({ title, rows, nameLabel }) {
  return (
    <Card title={title}>
      {!rows?.length ? (
        <div style={{ color: "var(--stone-700)" }}>No rollup data yet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{nameLabel}</th>
                <th>Events</th>
                <th>Tokens</th>
                <th>Cost</th>
                <th>Duration ms</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{formatNumber(row.count)}</td>
                  <td>{formatNumber(row.totalTokens)}</td>
                  <td>{formatUsd(row.estimatedCostUsd)}</td>
                  <td>{formatNumber(row.durationMs || row.modelDurationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString() : "0";
}

function formatMs(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return "n/a";
  if (number >= 1000) return `${(number / 1000).toFixed(2)}s`;
  return `${Math.round(number)} ms`;
}

function formatUsd(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? `$${number.toFixed(4)}` : "n/a";
}

export default function AnalyticsPanel() {
  const [filters, setFilters] = useState(defaultWindow);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [detailView, setDetailView] = useState("promptType");

  const totals = summary?.totals || {};
  const usage = summary?.usage || {};
  const detailRows = useMemo(() => {
    if (detailView === "status") return summary?.byStatus || [];
    if (detailView === "model") return summary?.byModel || [];
    if (detailView === "route") return summary?.rollups?.route || [];
    if (detailView === "step") return summary?.rollups?.step || [];
    if (detailView === "flowStage") return summary?.rollups?.flowStage || [];
    return summary?.byPromptType || [];
  }, [summary, detailView]);

  const routeRows = summary?.rollups?.route || [];
  const stepRows = summary?.rollups?.step || [];
  const flowStageRows = summary?.rollups?.flowStage || [];
  const coldStartRows = summary?.rollups?.coldStart || [];
  const dynamoTableRows = summary?.rollups?.tableName || [];
  const dynamoOperationRows = summary?.rollups?.operation || [];

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await analyticsApi.opsSummary({
        from: filters.from,
        to: filters.to,
        limit: filters.limit,
      });
      setSummary(res.data);
    } catch (e) {
      setToast({ message: e.message || "Unable to load analytics", tone: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="analytics-shell">
      <Card
        title="Operational analytics"
        action={<Button onClick={fetchSummary} disabled={loading}>{loading ? "Loading..." : "Refresh"}</Button>}
        tone="accent"
      >
        <div className="analytics-filter-grid">
          <Input label="From" type="date" value={filters.from} onChange={(from) => setFilters({ ...filters, from })} />
          <Input label="To" type="date" value={filters.to} onChange={(to) => setFilters({ ...filters, to })} />
          <Input label="Limit" type="number" value={filters.limit} onChange={(limit) => setFilters({ ...filters, limit })} />
          <Select
            label="Breakdown"
            value={detailView}
            onChange={setDetailView}
            options={[
              { label: "Prompt type", value: "promptType" },
              { label: "Status", value: "status" },
              { label: "Model", value: "model" },
              { label: "Route", value: "route" },
              { label: "Backend step", value: "step" },
              { label: "SMS flow stage", value: "flowStage" },
            ]}
          />
        </div>
        <div className="analytics-stat-grid">
          <Stat label="Unique active users" value={usage.available ? formatNumber(usage.uniqueActiveUsers) : "n/a"} />
          <Stat label="Unique sessions" value={usage.available ? formatNumber(usage.uniqueSessions) : "n/a"} />
          <Stat label="Prompt runs" value={formatNumber(totals.count)} />
          <Stat label="Errors" value={formatNumber(totals.errorCount)} tone={Number(totals.errorCount || 0) > 0 ? "warn" : "neutral"} />
          <Stat label="Tokens" value={formatNumber(totals.totalTokens)} />
          <Stat label="LLM cost" value={formatUsd(totals.estimatedCostUsd)} />
          <Stat label="Cost / inbound" value={formatUsd(totals.costPerInboundMessageUsd)} />
          <Stat label="Avg model latency" value={totals.avgModelDurationMs ? `${totals.avgModelDurationMs} ms` : "n/a"} />
          <Stat label="Slowest route p95" value={formatMs(p95Duration(topRows(routeRows, 1, p95Duration)[0]))} />
        </div>
      </Card>

      <MetricTable
        title={
          detailView === "status" ? "Runs by status"
            : detailView === "model" ? "Runs by model"
              : detailView === "route" ? "Events by route"
                : detailView === "step" ? "Events by backend step"
                  : detailView === "flowStage" ? "Events by SMS flow stage"
                    : "Runs by prompt type"
        }
        rows={detailRows}
        nameLabel={
          detailView === "status" ? "Status"
            : detailView === "model" ? "Model"
              : detailView === "route" ? "Route"
                : detailView === "step" ? "Step"
                  : detailView === "flowStage" ? "Flow stage"
                    : "Prompt type"
        }
      />

      <div className="analytics-insight-grid">
        <BarList title="Slowest routes" rows={routeRows} />
        <BarList title="Slowest backend steps" rows={stepRows} />
        <BarList title="SMS flow latency" rows={flowStageRows} />
        <ColdStartSplit rows={coldStartRows} />
        <BarList title="DynamoDB latency by table" rows={dynamoTableRows} />
        <BarList title="DynamoDB latency by operation" rows={dynamoOperationRows} />
      </div>

      <div className="analytics-rollup-grid">
        <RollupTable title="Events by type" rows={summary?.rollups?.eventType || []} nameLabel="Event type" />
        <RollupTable title="Events by channel" rows={summary?.rollups?.messagingChannel || []} nameLabel="Channel" />
        <RollupTable title="Events by tool" rows={summary?.rollups?.toolType || []} nameLabel="Tool" />
        <RollupTable title="Events by status" rows={summary?.rollups?.status || []} nameLabel="Status" />
        <RollupTable title="DynamoDB by table" rows={summary?.rollups?.tableName || []} nameLabel="Table" />
        <RollupTable title="DynamoDB by operation" rows={summary?.rollups?.operation || []} nameLabel="Operation" />
        <RollupTable title="Routes" rows={routeRows} nameLabel="Route" />
        <RollupTable title="Backend steps" rows={stepRows} nameLabel="Step" />
        <RollupTable title="SMS flow stages" rows={flowStageRows} nameLabel="Flow stage" />
        <RollupTable title="Cold starts" rows={coldStartRows} nameLabel="Cold start" />
        <RollupTable title="Entitlement tiers" rows={summary?.rollups?.entitlementTier || []} nameLabel="Tier" />
        <RollupTable title="Product outcomes" rows={summary?.rollups?.outcome || []} nameLabel="Outcome" />
        <RollupTable title="Safety decisions" rows={summary?.rollups?.guardDecision || []} nameLabel="Decision" />
        <RollupTable title="Media types" rows={summary?.rollups?.mediaType || []} nameLabel="Media type" />
      </div>

      <Card title="Recent errors">
        {!summary?.recentErrors?.length ? (
          <div style={{ color: "var(--stone-700)" }}>No recent prompt errors in this window.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {summary.recentErrors.map((error) => (
              <div className="analytics-error-row" key={`${error.traceId}-${error.createdAt}-${error.promptType}`}>
                <div>
                  <strong>{error.promptType || "UNKNOWN"}</strong>
                  <span>{error.status}</span>
                </div>
                <code>{error.createdAt}</code>
                <p>{error.errorCode || error.errorType || "Unclassified operational error"}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="analytics-privacy-note">
        Utilization is aggregate and pseudonymous. Athlete messages, media interpretations, transcripts,
        email addresses, and raw exception text are excluded from this dashboard.
      </div>

      <JsonPreview data={summary} />
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
